/**
 * KAN-738: обновление FEED-источника.
 *
 * - ночное обновление (auto_update: off | daily) через PATCH источника;
 * - полное обновление по кнопке: каталог пересобирается по фиду, лишние товары
 *   удаляются, промпты пересобираются (POST reindex).
 *
 * После любой мутации обновляются карточка источника и список источников.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { dataSourcesApi } from '@/services/api/dataSources';
import type { DataSource, FeedAutoUpdate } from '@/types/dataSource';

export const dataSourceQueryKey = (id: string | undefined) => ['dataSource', id] as const;

/** Ночное обновление включено только при явном 'daily'. */
export function isAutoUpdateEnabled(source: Pick<DataSource, 'auto_update'>): boolean {
  return source.auto_update === 'daily';
}

/** Переключатель и полное обновление доступны только FEED-источникам. */
export function isFeedSource(source: Pick<DataSource, 'type'>): boolean {
  return source.type === 'FEED';
}

/** Ночное обновление возможно только у фида по ссылке (не у загруженного файла). */
export function supportsAutoUpdate(source: Pick<DataSource, 'type' | 'url'>): boolean {
  return isFeedSource(source) && !!source.url;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Неизвестная ошибка';
}

export function useFeedSync(id: string) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: dataSourceQueryKey(id) });
    void queryClient.invalidateQueries({ queryKey: ['dataSources'] });
  };

  const setAutoUpdate = useMutation({
    mutationFn: (value: FeedAutoUpdate) => dataSourcesApi.setAutoUpdate(id, value),
    onSuccess: (updated, value) => {
      queryClient.setQueryData<DataSource>(dataSourceQueryKey(id), (prev) =>
        prev ? { ...prev, ...updated, auto_update: updated?.auto_update ?? value } : prev
      );
      invalidate();
      toast.success(
        value === 'daily' ? 'Ночное обновление включено' : 'Ночное обновление выключено'
      );
    },
    onError: (error) => {
      toast.error(`Не удалось сохранить настройку: ${errorText(error)}`);
    },
  });

  const fullRefresh = useMutation({
    mutationFn: () => dataSourcesApi.reindex(id),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['indexingRun'] });
      toast.success('Полное обновление запущено');
    },
    onError: (error) => {
      toast.error(`Не удалось запустить обновление: ${errorText(error)}`);
    },
  });

  return { setAutoUpdate, fullRefresh };
}
